// Auth state: current user, login/logout, the BTZL "active client" switcher, and
// the set of pages this user may see (the admin can hide pages from a client).
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api } from "../lib/api";
import { PAGE_KEYS, type MeResponse, type PageKey, type SessionUser } from "@shared/types";

interface AuthState {
  user: SessionUser | null;
  loading: boolean;
  // For BTZL: the client currently in focus (null = all clients). Client users
  // are always pinned to their own client.
  activeClientId: number | null;
  setActiveClientId: (id: number | null) => void;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  isBtzl: boolean;
  visiblePages: PageKey[];
  canSee: (page: PageKey) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeClientId, setActiveClientId] = useState<number | null>(null);
  const [visiblePages, setVisiblePages] = useState<PageKey[]>([...PAGE_KEYS]);

  useEffect(() => {
    api
      .get<MeResponse>("/api/auth/me")
      .then((d) => {
        setUser(d.user);
        setVisiblePages(d.visiblePages);
      })
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const d = await api.post<{ user: SessionUser }>("/api/auth/login", { email, password });
    setUser(d.user);
    setActiveClientId(d.user.role === "client" ? d.user.clientId : null);
    // Pull the page-access list for this user.
    try {
      const me = await api.get<MeResponse>("/api/auth/me");
      setVisiblePages(me.visiblePages);
    } catch {
      setVisiblePages([...PAGE_KEYS]);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/api/auth/logout");
    } finally {
      setUser(null);
      setActiveClientId(null);
      setVisiblePages([...PAGE_KEYS]);
    }
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      activeClientId: user?.role === "client" ? user.clientId : activeClientId,
      setActiveClientId,
      login,
      logout,
      isBtzl: user?.role === "btzl",
      visiblePages,
      canSee: (page: PageKey) => visiblePages.includes(page),
    }),
    [user, loading, activeClientId, login, logout, visiblePages]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
